import { images } from '../images';
import type { Course } from '../../types/content';

export const shortCourses: Course[] = [
  {
    slug: 'short-english-course',
    name: 'English',
    group: 'short',
    season: 'seasonal',
    keyword: 'short english course',
    metaTitle: 'Short English Course | One or Two Weeks | ES World',
    metaDescription: 'Take a short English course of one or two weeks. Practise speaking, join trips and meet new people. See seasonal dates.',
    h1: 'Short English course for a busy holiday',
    lead: 'Our short English course gives you one or two weeks of focused English practice during your holiday. You take lessons in the morning and have free time or activities in the afternoon. It suits adults who want to improve their English and see a new place at the same time.',
    cardText: 'One or two weeks of English during your holiday.',
    audience: 'Adults, 18+',
    image: images.social,
    facts: [
      { label: 'Course length', value: '1 to 2 weeks' },
      { label: 'Lessons', value: '15 a week' },
      { label: 'Levels', value: 'Elementary to advanced' },
      { label: 'Dates', value: 'Summer and winter' },
    ],
    valueTitle: 'Make quick, clear progress',
    valueText: 'Lessons focus on speaking and listening, so you notice progress in a short time. Afternoon activities give you more practice.',
    outcomes: [
      'Speak more often and with more confidence',
      'Learn useful phrases for travel and daily life',
      'Understand people more easily',
      'Meet students from other countries',
      'Join trips and activities in English',
      'Receive a certificate at the end of the course',
    ],
    whoTitle: 'For holiday learners',
    whoText: 'This course suits adults who have a short break and want to use it for English. You take a short online level test before you start.',
    whoFor: [
      { title: 'Holiday travellers', text: 'You want to learn while you visit a new place.' },
      { title: 'Busy professionals', text: 'You only have one or two weeks free.' },
      { title: 'Returning learners', text: 'You want to refresh your English quickly.' },
      { title: 'Friends and couples', text: 'You want to travel and study together.' },
    ],
    weekTitle: 'A sample short course week',
    weekText: 'Lessons run in the morning. Afternoons are free or used for optional activities with other students.',
    week: [
      { day: 'Monday', focus: 'Welcome, level check and speaking practice' },
      { day: 'Tuesday', focus: 'Lessons, then a city walk' },
      { day: 'Wednesday', focus: 'Lessons, then free time' },
      { day: 'Thursday', focus: 'Lessons, then a cultural visit' },
      { day: 'Friday', focus: 'Review and certificate' },
    ],
    faqs: [
      { q: 'Can I stay longer?', a: 'Yes. You can add more weeks or move to a year-round course such as General English.' },
      { q: 'Is accommodation available?', a: 'Yes. Our team can book a room near the campus for your dates.' },
    ],
    ctas: {
      heroPrimary: 'Ask about short course dates',
      heroSecondary: 'See a sample week',
      midPrimary: 'Get my short course price',
      midLink: { label: 'Compare with General English', to: '/programmes/general-english' },
      bottomPrimary: 'Send my short course enquiry',
    },
    locations: ['dubai', 'london', 'oxfordshire', 'new-york', 'new-haven', 'los-angeles', 'tokyo', 'seoul', 'hong-kong', 'cape-town'],
  },
  {
    slug: 'spanish-short-course',
    name: 'Spanish',
    group: 'short',
    season: 'seasonal',
    keyword: 'spanish short course',
    metaTitle: 'Spanish Short Course for Beginners and Improvers | ES World',
    metaDescription: 'Learn Spanish in a short course. Speak from the first lesson with a qualified teacher. See dates and sample prices.',
    h1: 'Spanish short course for beginners and improvers',
    lead: 'Our Spanish short course helps you start or improve your Spanish in a few weeks. You learn useful words and phrases for travel, work and daily life. Classes use the same practical ES World method, so you speak from the first lesson.',
    cardText: 'Start or improve your Spanish in a few weeks.',
    audience: 'Adults, 16+',
    image: images.classroom,
    facts: [
      { label: 'Course length', value: '000 weeks' },
      { label: 'Lessons', value: '000 a week' },
      { label: 'Levels', value: 'Beginner to intermediate' },
      { label: 'Dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Speak Spanish from day one',
    valueText: 'Lessons focus on real conversations. You practise in pairs and small groups, and your teacher helps you with pronunciation.',
    outcomes: [
      'Introduce yourself and talk about your life',
      'Order food, ask for directions and shop',
      'Understand simple conversations',
      'Use basic grammar correctly',
      'Read signs, menus and short texts',
      'Feel confident when you travel',
    ],
    whoTitle: 'For new Spanish learners',
    whoText: 'This course suits beginners and people who studied some Spanish before. You join a group at your level.',
    whoFor: [
      { title: 'Beginners', text: 'You have never studied Spanish before.' },
      { title: 'Travellers', text: 'You plan a trip to a Spanish-speaking country.' },
      { title: 'Professionals', text: 'You work with Spanish-speaking clients or colleagues.' },
      { title: 'Language lovers', text: 'You enjoy learning a new language.' },
    ],
    weekTitle: 'A sample Spanish week',
    weekText: 'Every lesson mixes new language with speaking practice. On Friday, you use everything in a short role play.',
    week: [
      { day: 'Monday', focus: 'Greetings and introductions' },
      { day: 'Tuesday', focus: 'Numbers, prices and shopping' },
      { day: 'Wednesday', focus: 'Food and ordering in a restaurant' },
      { day: 'Thursday', focus: 'Directions and travel' },
      { day: 'Friday', focus: 'Role play and review' },
    ],
    faqs: [
      { q: 'Are classes taught in Spanish?', a: 'Teachers use simple Spanish in class and explain difficult points in English when needed.' },
      { q: 'Can I take Spanish and English together?', a: 'Ask our team. A combined timetable may be possible for your dates.' },
    ],
    ctas: {
      heroPrimary: 'Ask about Spanish dates',
      heroSecondary: 'See the Spanish week',
      midPrimary: 'Get my Spanish course price',
      midLink: { label: 'Read about our teaching method', to: '/about/teaching-methodology' },
      bottomPrimary: 'Send my Spanish enquiry',
    },
    locations: ['dubai', 'london'],
  },
  {
    slug: 'digital-marketing-short-course',
    name: 'Digital Marketing',
    group: 'short',
    season: 'seasonal',
    keyword: 'short digital marketing course',
    metaTitle: 'Short Digital Marketing Course in English | ES World',
    metaDescription: 'Take a short digital marketing course in English. Learn social media and content basics in a few weeks. See dates.',
    h1: 'Short digital marketing course for beginners',
    lead: 'Our short digital marketing course teaches the basics of online marketing in a few weeks. You learn how to plan social media posts, write simple content and read basic results. All classes are in English, so you also practise professional English.',
    cardText: 'Learn the basics of online marketing quickly.',
    audience: 'Adults, 18+',
    image: images.hero,
    facts: [
      { label: 'Course length', value: '000 weeks' },
      { label: 'Hours', value: '000 a week' },
      { label: 'English level', value: 'Intermediate (B1) and up' },
      { label: 'Dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Learn marketing basics fast',
    valueText: 'Each session covers one topic and ends with a short practical task. You finish with a simple marketing plan.',
    outcomes: [
      'Plan a month of social media posts',
      'Write short content for websites',
      'Understand basic marketing words in English',
      'Read simple reports from social media',
      'Create a simple marketing plan',
      'Decide if the full course is right for you',
    ],
    whoTitle: 'For curious beginners',
    whoText: 'This course suits people who want to try digital marketing before a longer course. You need an intermediate English level.',
    whoFor: [
      { title: 'Small business owners', text: 'You want to market your business online.' },
      { title: 'Students', text: 'You want to test a possible career.' },
      { title: 'Career changers', text: 'You want a first look at marketing work.' },
      { title: 'Freelancers', text: 'You want to add a new skill.' },
    ],
    weekTitle: 'A sample short course week',
    weekText: 'Each day covers one topic. You practise with real examples and share your work with the group.',
    week: [
      { day: 'Monday', focus: 'What digital marketing is' },
      { day: 'Tuesday', focus: 'Social media planning' },
      { day: 'Wednesday', focus: 'Writing simple content' },
      { day: 'Thursday', focus: 'Reading basic results' },
      { day: 'Friday', focus: 'Present your marketing plan' },
    ],
    faqs: [
      { q: 'Do I need a laptop?', a: 'Yes. Please bring a laptop to every class.' },
      { q: 'Can I continue to the full course?', a: 'Yes. Many students continue to our full Digital Marketing course.' },
    ],
    ctas: {
      heroPrimary: 'Ask about the next short course',
      heroSecondary: 'See the course week',
      midPrimary: 'Get the short course price',
      midLink: { label: 'Explore the full course', to: '/programmes/digital-marketing' },
      bottomPrimary: 'Send my marketing question',
    },
    locations: ['dubai', 'london'],
  },
  {
    slug: 'ai-literacy',
    name: 'AI Literacy',
    group: 'short',
    season: 'seasonal',
    keyword: 'ai literacy course',
    metaTitle: 'AI Literacy Course | Use AI Tools Safely at Work | ES World',
    metaDescription: 'Take an AI literacy course in English. Learn to use AI tools safely and clearly at work. See dates and sample prices.',
    h1: 'AI literacy course for work and study',
    lead: 'Our AI literacy course helps you understand and use AI tools in a safe, clear way. You learn what AI can and cannot do, how to write good instructions and how to check results. All classes are in English, so you also learn the words you need to talk about technology.',
    cardText: 'Use AI tools safely for work and study.',
    audience: 'Adults, 18+',
    image: images.classroom,
    facts: [
      { label: 'Course length', value: '000 weeks' },
      { label: 'Hours', value: '000 a week' },
      { label: 'English level', value: 'Intermediate (B1) and up' },
      { label: 'Dates', value: '00 Month 0000' },
    ],
    valueTitle: 'Use AI with confidence',
    valueText: 'You practise with common AI tools on real tasks, such as writing emails or planning a project. You also learn when not to trust a result.',
    outcomes: [
      'Explain what AI tools can and cannot do',
      'Write clear instructions for an AI tool',
      'Check AI results for mistakes',
      'Protect private information when you use AI',
      'Use AI to plan, write and study',
      'Talk about technology in clear English',
    ],
    whoTitle: 'For everyday AI users',
    whoText: 'This course suits people who want to use AI at work or in study. You do not need any technical knowledge.',
    whoFor: [
      { title: 'Office workers', text: 'You want to save time on daily tasks.' },
      { title: 'Students', text: 'You want to use AI for study in a fair way.' },
      { title: 'Managers', text: 'You want to guide your team on AI use.' },
      { title: 'Teachers', text: 'You want to understand the tools your students use.' },
    ],
    weekTitle: 'A sample AI week',
    weekText: 'Each session mixes a short explanation with hands-on practice. You leave with a personal guide for using AI.',
    week: [
      { day: 'Monday', focus: 'What AI is and how it works' },
      { day: 'Tuesday', focus: 'Writing clear instructions' },
      { day: 'Wednesday', focus: 'Checking results and facts' },
      { day: 'Thursday', focus: 'Privacy and safe use' },
      { day: 'Friday', focus: 'Your personal AI guide' },
    ],
    faqs: [
      { q: 'Do I need to know how to code?', a: 'No. This course is for everyday users. You only need a laptop and an intermediate level of English.' },
      { q: 'Which tools do we use?', a: 'Tools may change. Your trainer chooses common tools that are free or easy to access.' },
    ],
    ctas: {
      heroPrimary: 'Ask about AI course dates',
      heroSecondary: 'See the AI week',
      midPrimary: 'Get my AI course price',
      midLink: { label: 'Improve your work English too', to: '/programmes/business-english' },
      bottomPrimary: 'Send my AI course enquiry',
    },
    locations: ['dubai', 'london', 'new-york'],
  },
];
